import {useQuery, useQueryClient} from '@github-ui/react-query'
import {useMemo} from 'react'
import type {Pull} from '../types/pull'

// Fetch a single pull request with optional diffs and body
export const fetchPullRequest = async (pullRequestId: number, options: {includeBody?: boolean} = {}): Promise<Pull> => {
  const {includeBody = false} = options
  const url = new URL('/copilot/agent-sessions/pull-requests', window.location.origin)
  url.searchParams.set('pull_request_ids', String(pullRequestId))

  if (includeBody) {
    url.searchParams.set('include_body', 'true')
  }

  const response = await fetch(url.toString(), {
    headers: {
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
  })

  if (!response.ok) {
    throw new Error(`Failed to fetch pull request ${pullRequestId}`)
  }

  const data = await response.json()
  const pullRequests = data.pull_requests
  const pullRequest = pullRequests[pullRequestId]

  if (!pullRequest) {
    throw new Error(`Pull request ${pullRequestId} not found`)
  }

  return pullRequest
}

export function usePullRequest({
  pullRequestId,
  includeBody = false,
  initialData,
  enabled = true,
  refetchOnMount = false,
}: {
  pullRequestId?: number
  includeBody?: boolean
  initialData?: Pull
  enabled?: boolean
  refetchOnMount?: boolean
}) {
  const queryClient = useQueryClient()
  const queryKey = useMemo(() => ['pull-request', pullRequestId, includeBody], [pullRequestId, includeBody])

  const queryFn = () => {
    if (!pullRequestId) {
      throw new Error('pullRequestId is required')
    }
    return fetchPullRequest(pullRequestId, {includeBody})
  }

  // Determine staleTime based on options:
  // - refetchOnMount: always refetch on mount (staleTime: 0)
  // - initialData provided: cache for 5 minutes before refetching
  // - no initialData: always fetch fresh data (staleTime: 0)
  const staleTime = refetchOnMount || !initialData ? 0 : 300000

  const {data, refetch: refetchAll} = useQuery<Pull>({
    queryKey,
    queryFn,
    initialData,
    staleTime,
    enabled: enabled && pullRequestId !== undefined,
  })

  const refetchBody = async () => {
    if (!pullRequestId) {
      throw new Error('pullRequestId is required')
    }
    const bodyData = await fetchPullRequest(pullRequestId, {includeBody: true})

    // Update the current query data with new body and body revision count
    queryClient.setQueryData(queryKey, (oldData: Pull | undefined) => {
      if (!oldData) return bodyData
      return {
        ...oldData,
        body: bodyData.body,
        body_html: bodyData.body_html,
        body_revision_count: bodyData.body_revision_count,
      }
    })
  }

  const refetchPullRequest = async () => {
    if (!pullRequestId) {
      throw new Error('pullRequestId is required')
    }
    const headerData = await fetchPullRequest(pullRequestId, {includeBody: false})

    // Update the current query data with new info
    queryClient.setQueryData(queryKey, (oldData: Pull | undefined) => {
      if (!oldData) return headerData
      return {
        ...oldData,
        title: headerData.title,
        head_sha: headerData.head_sha,
        changed_files: headerData.changed_files,
        commit_alive_channel: headerData.commit_alive_channel,
      }
    })
  }

  return {
    data,
    refetch: refetchAll,
    refetchBody,
    refetchPullRequest,
  }
}
